import React, { useEffect, useRef, useMemo, useState } from 'react';
import type { AnalysisData, Model3D, CaptureSequence, CapturedImage, Landmark, AnalysisResult, Settings } from './types';
import { useToast } from '@/hooks/use-toast';
import { HELP_TEXT, IDX } from './constants';
import { sevBadge, estimateCOM, classifyPosture, analyzeAnterior, analyzePosterior, analyzeLateral } from './lib/analysis';
import { filterMetricsByExperimental } from './lib/posture-metrics-filter';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableRow, TableHead, TableHeader } from "@/components/ui/table";
import { ClickableValueEnhanced } from './ClickableValue';
import MetricGauge from './MetricGauge';
import { getGaugeColors } from './lib/analysis';
import { rotateLandmarks, rotateCustomLandmarks } from '@/lib/posture-analysis/floor-correction';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { ClassificationDetails, type ClassificationResult, selectPosturePhotoTileClassification } from './ClassificationDetails';
import { usePostureMode } from '@/components/posture-analysis/lib/PostureModeContext';
import { shouldOmitPelvicTilt } from '@/components/posture-analysis/lib/posture-mode-strategy';
import { getLaymanViewReportTitle } from '@/components/posture-analysis/self-assessment/view-display-labels';
import { inferSagittalInputFromCaptured } from '@/lib/posture-analysis/posture-pic-evaluation/strategies/posture-strategy-context';
import { cn } from '@/lib/utils';

interface AnalysisPanelsProps {
    analysisData: AnalysisData;
    model3D: Model3D | null;
    capturedImages: Record<CaptureSequence, CapturedImage | null>;
    settings: Settings;
    currentView: CaptureSequence | null;
    variant?: 'full' | 'metricsOnly' | 'classificationOnly';
    /** When false, only angle metrics (°) are shown; distance metrics (cm/inches) are hidden. */
    experimentalFeaturesEnabled?: boolean;
    /** Passed through to `classifyPosture` for orientation algorithm selection. */
    postureOrientationStrategyId?: Settings['postureOrientationStrategyId'];
    /** Hide the view title header in metrics-only mode (mobile split). */
    suppressViewTitle?: boolean;
    /** Gauge density for compact metric lists. */
    metricsDensity?: 'compact' | 'dense';
}

const HelpButton: React.FC<{ topic: string, onHelp: (topic: string) => void }> = ({ topic, onHelp }) => (
    <Button 
        onClick={() => onHelp(topic)} 
        variant="outline" 
        size="icon" 
        className="absolute right-2 top-2 h-6 w-6 rounded-full text-xs"
        aria-label={`Get help about ${topic}`}
    >
        ?
    </Button>
);

const DigiCard: React.FC<{ title: string, topic: string, onHelp: (topic: string) => void, children: React.ReactNode, className?: string }> = ({ title, topic, onHelp, children, className }) => (
    <Card className={`shadow-md relative ${className}`}>
        <CardHeader>
            <CardTitle className="text-fg">{title}</CardTitle>
        </CardHeader>
        <CardContent>
            {children}
        </CardContent>
        <HelpButton topic={topic} onHelp={onHelp} />
    </Card>
);

// Helper to determine range for gauges
const getRangeForMetric = (unit: string, val: number): [number, number] => {
    if (unit === '°' || unit === 'deg') return [-15, 15]; 
    const absVal = Math.abs(val);
    // Dynamic range: at least +/- 5cm, or 1.5x the value if it's large
    const limit = Math.max(Math.ceil(absVal * 1.5), 5); 
    return [-limit, limit];
}

// Helper to get labels based on view or metric context
const getLabelsForMetric = (label: string, view?: CaptureSequence): { left: string, right: string } => {
    const l = label.toLowerCase();
    
    // Anterior View
    if (view === 'Anterior') {
         if (l.includes('tilt') || l.includes('level') || l.includes('shift') || l.includes('lean')) {
             return { left: 'R', right: 'L' }; // Patient's Right is Viewer's Left
         }
    }

    // Posterior View
    if (view === 'Posterior') {
        if (l.includes('tilt') || l.includes('level') || l.includes('shift') || l.includes('lean')) {
             return { left: 'L', right: 'R' }; // Patient's Left is Viewer's Left
        }
    }
    
    // Lateral Views
    if (view === 'Left') {
        // Side view looking from Left: Left is Back, Right is Front
        if (l.includes('shift') || l.includes('fwd') || l.includes('back') || l.includes('anterior')) {
            return { left: 'B', right: 'F' }; 
        }
        // Pelvic tilt: Post / Ant
        if (l.includes('pelvic tilt')) return { left: 'Post', right: 'Ant' };
        if (l.includes('inclination')) return { left: 'Post', right: 'Ant' };
    }

    if (view === 'Right') {
        // Side view looking from Right: Left is Back, Right is Front?
        // Wait. If looking at Right side profile:
        // Left of screen is patient's Back. Right of screen is patient's Front.
        // So same B - F logic?
        // Let's verify. Head is usually on top. 
        // If profile faces Right -> Left side of screen is Back.
        // If profile faces Left -> Left side of screen is Front.
        // Standard medical photos usually face Right (Right Profile).
        // But our CaptureSequence has 'Left' and 'Right' views.
        // 'Right' view usually means viewing the Right side of the patient. (Patient facing viewer's right? Or patient's right side visible?)
        // Usually "Right Lateral View" means we see the right side of the body. Patient is facing Right.
        // "Left Lateral View" means we see the left side. Patient is facing Left.
        
        // If patient faces RIGHT (Right View):
        // Left Screen = Back. Right Screen = Front.
        // So B - Bar - F.
        if (l.includes('shift') || l.includes('fwd') || l.includes('back') || l.includes('anterior')) {
             return { left: 'B', right: 'F' };
        }
         if (l.includes('pelvic tilt')) return { left: 'Post', right: 'Ant' };
    }

    // Fallbacks if view logic doesn't catch it
    if (l.includes('head') || l.includes('neck') || l.includes('shoulder') || l.includes('hip') || l.includes('knee')) {
        if (l.includes('shift') || l.includes('fwd') || l.includes('back') || l.includes('anterior')) {
            return { left: 'Back', right: 'Fwd' }; 
        }
        if (l.includes('tilt') || l.includes('level')) {
             return { left: 'L', right: 'R' };
        }
    }
    if (l.includes('flex') || l.includes('ext')) return { left: 'Ext', right: 'Flex' };
    
    return { left: '-', right: '+' };
}

/** Lateral (Left/Right) sagittal gauges: label order + mirroring so bar highlight matches formatted Ant/Post (and A/P). Right view mirrors Left layout. */
function getLateralSagittalGauge(
    view: 'Left' | 'Right',
    item: AnalysisResult
): { lLabel: string; rLabel: string; mirrored: boolean } | null {
    const lab = item.label;
    if (lab.includes('Craniovertebral')) {
        return view === 'Left'
            ? { lLabel: 'A', rLabel: 'P', mirrored: true }
            : { lLabel: 'P', rLabel: 'A', mirrored: false };
    }
    if (lab.includes('Shoulder Anterior Shift') || lab.includes('Hip Anterior Shift')) {
        return view === 'Left'
            ? { lLabel: 'A', rLabel: 'P', mirrored: false }
            : { lLabel: 'P', rLabel: 'A', mirrored: false };
    }
    if (lab.includes('Knee-Hip Offset')) {
        return view === 'Left'
            ? { lLabel: 'Ant', rLabel: 'Post', mirrored: false }
            : { lLabel: 'Post', rLabel: 'Ant', mirrored: true };
    }
    if (lab.includes('Tibial Inclination')) {
        return view === 'Left'
            ? { lLabel: 'Ant', rLabel: 'Post', mirrored: true }
            : { lLabel: 'Post', rLabel: 'Ant', mirrored: false };
    }
    if (lab.toLowerCase().includes('pelvic tilt')) {
        return view === 'Left'
            ? { lLabel: 'Ant', rLabel: 'Post', mirrored: true }
            : { lLabel: 'Post', rLabel: 'Ant', mirrored: false };
    }
    return null;
}

function getDisplayMetricLabel(view: CaptureSequence, label: string): string {
    if ((view === 'Left' || view === 'Right') && label.includes('Craniovertebral')) {
        return 'CV Angle (Head fwd)';
    }
    return label;
}

/**
 * Centralised gauge label + mirror resolution shared by the table and compact list.
 * Display-only: never changes evaluation values, only how the bar/labels read.
 */
function resolveGaugeLabels(
    view: CaptureSequence,
    item: AnalysisResult
): { lLabel: string; rLabel: string; mirrored: boolean } {
    const labels = getLabelsForMetric(item.label, view);
    let lLabel = labels.left;
    let rLabel = labels.right;

    if (view === 'Left' || view === 'Right') {
        const lateral = getLateralSagittalGauge(view, item);
        if (lateral) return lateral;
        if (item.label.includes('Knee State')) {
            return view === 'Left'
                ? { lLabel: 'Flex', rLabel: 'Hyp', mirrored: false }
                : { lLabel: 'Hyp', rLabel: 'Flex', mirrored: true };
        }
        if (item.label.includes('Trunk') && item.label.includes('Hip Offset')) {
            return { lLabel: 'Post', rLabel: 'Ant', mirrored: false };
        }
        return { lLabel, rLabel, mirrored: false };
    }

    if (view === 'Anterior') {
        const lower = item.label.toLowerCase();
        const mirrored = lower.includes('tilt') || lower.includes('level');
        return { lLabel: 'R', rLabel: 'L', mirrored };
    }

    if (view === 'Posterior') {
        if (
            item.label.includes('Tilt') ||
            item.label.includes('Level') ||
            item.label.includes('Shift') ||
            item.label.includes('Lean')
        ) {
            return { lLabel: 'L', rLabel: 'R', mirrored: false };
        }
    }

    return { lLabel, rLabel, mirrored: false };
}

export function translateLaymanMetricLabel(label: string): string {
    if (label === 'P') return 'B';
    if (label === 'A') return 'F';
    if (label === 'Post') return 'Back';
    if (label === 'Ant') return 'Front';
    if (label === 'Posterior') return 'Back';
    if (label === 'Anterior') return 'Front';
    return label;
}

export function translateFormattedValue(formattedValue: string): string {
    if (!formattedValue) return formattedValue;
    return formattedValue
        .replace(/\bPost\b/g, 'Back')
        .replace(/\bAnt\b/g, 'Front')
        .replace(/\bPosterior\b/g, 'Back')
        .replace(/\bAnterior\b/g, 'Front');
}

export const AnalysisReportTable: React.FC<{ data: AnalysisResult[], view: CaptureSequence }> = ({ data, view }) => {
    if (!data || data.length === 0) return null;
    
    const { laymanMode } = usePostureMode();
    const gaugeColors = getGaugeColors();
    const shouldShowPelvicTiltEstimateNote = (item: AnalysisResult): boolean => {
        if (!(view === 'Left' || view === 'Right')) return false;
        const isPelvicTilt = item.label.toLowerCase().includes('pelvic tilt');
        const isEstimated = (item.formattedValue || '').includes('(Est.)');
        return isPelvicTilt && isEstimated;
    };

    // Special handling for Calcaneus Tilt in Posterior view to combine them
    const processedData = [...data];
    let combinedCalcaneus = null;

    if (view === 'Posterior') {
        // Case-insensitive or exact match based on what we found in analysis.tsx: 'Calcaneus tilt (L)'
        const calcLIndex = processedData.findIndex(d => d.label.toLowerCase() === 'calcaneus tilt (l)');
        const calcRIndex = processedData.findIndex(d => d.label.toLowerCase() === 'calcaneus tilt (r)');

        if (calcLIndex !== -1 && calcRIndex !== -1) {
            const calcL = processedData[calcLIndex];
            const calcR = processedData[calcRIndex];
            
            // Remove the individual items
            // We do this carefully to handle indices shifting. 
            // Better to filter them out and insert the combined one.
            combinedCalcaneus = { left: calcL, right: calcR };
        }
    }

    const filteredData = view === 'Posterior' && combinedCalcaneus 
        ? processedData.filter(d => d.label.toLowerCase() !== 'calcaneus tilt (l)' && d.label.toLowerCase() !== 'calcaneus tilt (r)')
        : processedData;

    return (
         <Table>
            <TableHeader>
                <TableRow>
                    <TableHead className="w-[40%]">Metric</TableHead>
                    <TableHead className="w-[60%] text-center">Analysis</TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
                {filteredData.map((item) => {
                    const { lLabel, rLabel, mirrored: isMirrored } = resolveGaugeLabels(view, item);
                    const displayLabel = getDisplayMetricLabel(view, item.label);
                    const finalLLabel = laymanMode ? translateLaymanMetricLabel(lLabel) : lLabel;
                    const finalRLabel = laymanMode ? translateLaymanMetricLabel(rLabel) : rLabel;
                    const displayFormattedValue = laymanMode ? translateFormattedValue(item.formattedValue) : item.formattedValue;

                    return (
                        <TableRow key={item.label}>
                            <TableCell className="font-medium text-base align-middle">{displayLabel}</TableCell>
                            <TableCell>
                                <MetricGauge 
                                    value={item.raw}
                                    formattedValue={displayFormattedValue}
                                    unit={item.unit || ''}
                                    severity={item.sev as 0|1|2|3}
                                    range={getRangeForMetric(item.unit || '', item.raw)}
                                    leftLabel={finalLLabel}
                                    rightLabel={finalRLabel}
                                    mirrored={isMirrored}
                                    colors={gaugeColors}
                                />
                                {shouldShowPelvicTiltEstimateNote(item) && (
                                    <div className="mt-1 text-xs text-status-warn">
                                        Pelvic tilt is estimated until ASIS/PSIS are pinned.
                                    </div>
                                )}
                            </TableCell>
                        </TableRow>
                    );
                })}
                
                {/* Render Combined Calcaneus Tilt for Posterior View */}
                {combinedCalcaneus && (
                    <TableRow key="combined-calcaneus">
                        <TableCell colSpan={2} className="p-4">
                            <div className="flex flex-col items-center gap-2 w-full">
                                {/* Header with Left and Right on sides */}
                                <div className="flex items-center justify-between w-full px-4 mb-1">
                                    <span className="text-lg font-bold text-muted">Left</span>
                                    <span className="font-bold text-base">Calcaneus Tilt</span>
                                    <span className="text-lg font-bold text-muted">Right</span>
                                </div>
                                
                                <div className="flex w-full gap-8">
                                    {/* Left Foot: Inv - Bar - Ev */}
                                    <div className="flex-1">
                                        <MetricGauge 
                                            value={combinedCalcaneus.left.raw}
                                            formattedValue={combinedCalcaneus.left.formattedValue}
                                            unit={combinedCalcaneus.left.unit || ''}
                                            severity={combinedCalcaneus.left.sev as 0|1|2|3}
                                            range={getRangeForMetric(combinedCalcaneus.left.unit || '', combinedCalcaneus.left.raw)}
                                            leftLabel="Inv"
                                            rightLabel="Ev"
                                            mirrored={true}
                                            colors={gaugeColors}
                                        />
                                    </div>
                                    {/* Right Foot: Ev - Bar - Inv */}
                                    <div className="flex-1">
                                         <MetricGauge 
                                            value={combinedCalcaneus.right.raw}
                                            formattedValue={combinedCalcaneus.right.formattedValue}
                                            unit={combinedCalcaneus.right.unit || ''}
                                            severity={combinedCalcaneus.right.sev as 0|1|2|3}
                                            range={getRangeForMetric(combinedCalcaneus.right.unit || '', combinedCalcaneus.right.raw)}
                                            leftLabel="Ev"
                                            rightLabel="Inv"
                                            colors={gaugeColors}
                                        />
                                    </div>
                                </div>
                            </div>
                        </TableCell>
                    </TableRow>
                )}
            </TableBody>
         </Table>
    );
};

const AnalysisReportCompactList: React.FC<{
  data: AnalysisResult[];
  view: CaptureSequence;
  density?: 'compact' | 'dense';
}> = ({ data, view, density = 'compact' }) => {
    if (!data || data.length === 0) return null;

    const { laymanMode } = usePostureMode();
    const gaugeColors = getGaugeColors();
    const shouldShowPelvicTiltEstimateNote = (item: AnalysisResult): boolean => {
        if (!(view === 'Left' || view === 'Right')) return false;
        const isPelvicTilt = item.label.toLowerCase().includes('pelvic tilt');
        const isEstimated = (item.formattedValue || '').includes('(Est.)');
        return isPelvicTilt && isEstimated;
    };

    // Special handling for Calcaneus Tilt in Posterior view to combine them
    const processedData = [...data];
    let combinedCalcaneus = null as null | { left: AnalysisResult; right: AnalysisResult };

    if (view === 'Posterior') {
        const calcLIndex = processedData.findIndex(d => d.label.toLowerCase() === 'calcaneus tilt (l)');
        const calcRIndex = processedData.findIndex(d => d.label.toLowerCase() === 'calcaneus tilt (r)');
        if (calcLIndex !== -1 && calcRIndex !== -1) {
            combinedCalcaneus = { left: processedData[calcLIndex], right: processedData[calcRIndex] };
        }
    }

    const filteredData = view === 'Posterior' && combinedCalcaneus
        ? processedData.filter(d => d.label.toLowerCase() !== 'calcaneus tilt (l)' && d.label.toLowerCase() !== 'calcaneus tilt (r)')
        : processedData;

    const renderGauge = (item: AnalysisResult) => {
        const { lLabel, rLabel, mirrored: isMirrored } = resolveGaugeLabels(view, item);
        const finalLLabel = laymanMode ? translateLaymanMetricLabel(lLabel) : lLabel;
        const finalRLabel = laymanMode ? translateLaymanMetricLabel(rLabel) : rLabel;
        const displayFormattedValue = laymanMode ? translateFormattedValue(item.formattedValue) : item.formattedValue;

        return (
            <MetricGauge
                value={item.raw}
                formattedValue={displayFormattedValue}
                unit={item.unit || ''}
                severity={item.sev as 0 | 1 | 2 | 3}
                range={getRangeForMetric(item.unit || '', item.raw)}
                leftLabel={finalLLabel}
                rightLabel={finalRLabel}
                mirrored={isMirrored}
                density={density === 'dense' ? 'dense' : 'compact'}
                colors={gaugeColors}
            />
        );
    };

    const isDense = density === 'dense';

    return (
        <div className={cn('flex flex-col', isDense ? 'gap-1.5' : 'gap-3')}>
            {filteredData.map((item) => (
                <div key={item.label} className={cn('rounded-lg border border-line bg-bg', isDense ? 'p-1.5' : 'p-2')}>
                    <div className={cn('font-medium text-fg mb-1', isDense ? 'text-[11px]' : 'text-xs')}>{getDisplayMetricLabel(view, item.label)}</div>
                    <div className="min-w-0">
                        {renderGauge(item)}
                    </div>
                        {shouldShowPelvicTiltEstimateNote(item) && (
                            <div className="mt-1 text-[11px] text-status-warn">
                                Pelvic tilt is estimated until ASIS/PSIS are pinned.
                            </div>
                        )}
                </div>
            ))}

            {combinedCalcaneus && (
                <div className="rounded-lg border border-line bg-bg px-3 py-3">
                    <div className="flex items-center justify-between mb-2">
                        <div className="text-xs font-medium text-fg">Calcaneus Tilt</div>
                        <div className="text-[10px] text-muted">Inv ↔ Ev</div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="min-w-0">
                            <div className="text-[10px] text-muted mb-1">Left</div>
                            {renderGauge(combinedCalcaneus.left)}
                        </div>
                        <div className="min-w-0">
                            <div className="text-[10px] text-muted mb-1">Right</div>
                            {renderGauge(combinedCalcaneus.right)}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const AnalysisPanels: React.FC<AnalysisPanelsProps> = ({
  analysisData,
  model3D,
  capturedImages,
  settings,
  currentView,
  variant = 'full',
  experimentalFeaturesEnabled = false,
  postureOrientationStrategyId,
  suppressViewTitle = false,
  metricsDensity = 'compact',
}) => {
    const { toast } = useToast();
    const postureMode = usePostureMode();
    const { laymanMode } = postureMode;
    const [classification, setClassification] = useState<ClassificationResult | null>(null);
    const [showClassification, setShowClassification] = useState(false);
    const photoClassification = selectPosturePhotoTileClassification(classification);
    const showDetailSections = !laymanMode;
    const viewReportTitle = currentView
      ? laymanMode
        ? getLaymanViewReportTitle(currentView)
        : `${currentView} View Report`
      : laymanMode
        ? 'View measurements'
        : 'View Report';

    const handleHelp = (topic: string) => {
        toast({ description: `Help for: ${topic}` });
        alert(HELP_TEXT[topic] || "No help available.");
    };

    const detailedAnalysis = useMemo(() => {
        if (!analysisData) return null;
        if (variant === 'classificationOnly') return null;

        const result: Partial<Record<CaptureSequence, AnalysisResult[]>> = {};
        const { unit } = settings;

        for (const view of Object.keys(capturedImages) as CaptureSequence[]) {
            if (view === 'Posterior' && !experimentalFeaturesEnabled) continue;
            const data = capturedImages[view];
            if (data?.landmarks && data.cmPerPx) {
                // Floor correction feeds the gauges by rotating inputs.
                const deg = data.floorCorrectionDeg ?? 0;
                const pivot = data.floorCorrectionPivot || undefined;
                const lm = rotateLandmarks(data.landmarks, deg, pivot);
                const custom = rotateCustomLandmarks(data.custom, deg, pivot);
                let metrics: AnalysisResult[];
                if (view === 'Anterior') {
                    metrics = analyzeAnterior(lm, view, data.cmPerPx, unit, custom);
                } else if (view === 'Posterior') {
                    metrics = analyzePosterior(lm, view, data.cmPerPx, unit, custom);
                } else if (view === 'Left' || view === 'Right') {
                    metrics = analyzeLateral(lm, view, data.cmPerPx, unit, custom, {
                        omitPelvicTilt: shouldOmitPelvicTilt(postureMode, data),
                    });
                } else {
                    continue;
                }
                result[view] = filterMetricsByExperimental(metrics, experimentalFeaturesEnabled);
            }
        }
        return result;
    }, [capturedImages, settings, analysisData, variant, experimentalFeaturesEnabled, postureMode]);

    useEffect(() => {
        // Classification isn't needed for metrics-only drawer
        if (variant === 'metricsOnly') {
            setClassification(null);
            return;
        }
        if (analysisData && Object.keys(analysisData).length > 0) {
            const { sagittal } = inferSagittalInputFromCaptured(capturedImages);
            classifyPosture(analysisData, undefined, {
                strategyId: postureOrientationStrategyId ?? undefined,
                strategyInput: sagittal ? { sagittal } : undefined,
                capturedForStrategy: capturedImages,
            }).then((res: any) => setClassification(res));
        } else {
            setClassification(null);
        }
    }, [analysisData, variant, capturedImages, postureOrientationStrategyId]);

    if (variant === 'classificationOnly') {
        return (
            <div className="w-full">
                <Card className="shadow-md">
                    <CardHeader>
                        <CardTitle className="text-fg">Posture Classification</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <ClassificationDetails classification={photoClassification} showDetailSections={showDetailSections} />
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
      <div className={cn('flex flex-col', variant === 'metricsOnly' ? 'gap-0' : 'gap-4')}>
        
        <div
          id="analysis-container"
          className={variant === 'full' ? 'mt-4' : undefined}
        >
            {currentView ? (
                <Card className={cn(
                  'shadow-md relative',
                  variant === 'metricsOnly' ? 'border-0 shadow-none' : 'overflow-hidden',
                )}>
                    {!suppressViewTitle && (
                    <CardHeader className={variant === 'metricsOnly' ? 'flex flex-row items-center justify-between px-3 pt-3 pb-2' : 'flex flex-row items-center justify-between pb-2'}>
                        <CardTitle className={variant === 'metricsOnly' && laymanMode ? 'text-fg text-base leading-snug' : 'text-fg'}>{viewReportTitle}</CardTitle>
                        {!laymanMode && (
                          <div className="flex items-center space-x-2">
                              <HelpButton topic={`panel-${currentView}`} onHelp={handleHelp} />
                          </div>
                        )}
                    </CardHeader>
                    )}
                    <CardContent className={variant === 'metricsOnly' ? cn(suppressViewTitle ? 'px-2 pb-2 pt-2' : 'px-3 pb-4 pt-0') : undefined}>
                        {detailedAnalysis && detailedAnalysis[currentView] ? (
                            variant === 'metricsOnly'
                                ? <AnalysisReportCompactList data={detailedAnalysis[currentView]!} view={currentView} density={metricsDensity} />
                                : <AnalysisReportTable data={detailedAnalysis[currentView]!} view={currentView} />
                        ) : <p className="text-sm text-muted">Processing {currentView} view...</p>}

                        {variant === 'full' && (
                            <div className="mt-8 pt-4 border-t">
                                <div className="flex items-center justify-end space-x-2 mb-4">
                                    <Label htmlFor="show-classification" className="text-sm font-medium text-muted">Show Classification</Label>
                                    <Switch 
                                        id="show-classification" 
                                        checked={showClassification} 
                                        onCheckedChange={setShowClassification} 
                                    />
                                </div>
                                
                                {showClassification && (
                                    <div className="animate-in fade-in slide-in-from-top-4 duration-300">
                                        <h3 className="text-lg font-semibold mb-4">Posture Classification</h3>
                                        <ClassificationDetails classification={photoClassification} showDetailSections={showDetailSections} />
                                    </div>
                                )}
                            </div>
                        )}
                    </CardContent>
                </Card>
            ) : (
                <Card className={`shadow-md ${variant === 'metricsOnly' ? 'h-full' : ''}`}>
                    <CardHeader>
                        <CardTitle className="text-fg">{laymanMode ? 'View measurements' : 'View Report'}</CardTitle>
                    </CardHeader>
                    <CardContent className={variant === 'metricsOnly' ? 'h-full flex items-center justify-center' : undefined}>
                        <p className="text-sm text-muted">Select a view from the capture status buttons to see its analysis report.</p>
                        
                        {variant === 'full' && (
                            <div className="mt-8 pt-4 border-t">
                                 <div className="flex items-center justify-end space-x-2 mb-4">
                                    <Label htmlFor="show-classification" className="text-sm font-medium text-muted">Show Classification</Label>
                                    <Switch 
                                        id="show-classification" 
                                        checked={showClassification} 
                                        onCheckedChange={setShowClassification} 
                                    />
                                </div>
                                
                                 {showClassification && (
                                    <div className="animate-in fade-in slide-in-from-top-4 duration-300">
                                        <h3 className="text-lg font-semibold mb-4">Posture Classification</h3>
                                        <ClassificationDetails classification={photoClassification} showDetailSections={showDetailSections} />
                                    </div>
                                )}
                            </div>
                        )}
                    </CardContent>
                </Card>
            )}
        </div>
      </div>
    );
};

export default AnalysisPanels;